import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  ButtonGroup,
  Tabs,
  Tab,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  Alert,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Slide,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Visibility as ViewIcon,
  Description as InvoiceIcon,
  FilterList as FilterIcon,
  VerifiedUser as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon,
  FileDownload as ExportIcon,
  DateRange as DateRangeIcon,
  ArrowUpward as ArrowUpwardIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productService } from '../utils/apiService';
import ConfirmationModal from '../components/print/ConfirmationModal';
import ExportButton from '../components/ExportButton';
import * as XLSX from 'xlsx';

const Items = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    status: {
      active: true,
      inactive: true,
    },
    group: {
      electronics: true,
      services: true,
      furniture: true,
      other: true
    },
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);   // SheetJS parsed data
  const [parsedHeaders, setParsedHeaders] = useState([]);  // column headers

  // Field mapping state
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [bulkFields, setBulkFields] = useState([]);      // API fields from GET /products/bulk/fields
  const [bulkFieldsLoading, setBulkFieldsLoading] = useState(false);
  const [bulkFieldsError, setBulkFieldsError] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});  // { apiField: excelHeader | '' }

  // Toast / Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage, searchQuery, activeTab]);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      let itemType = '';
      if (activeTab === 1) itemType = 'goods';
      else if (activeTab === 2) itemType = 'service';

      const response = await productService.getProducts(page + 1, rowsPerPage, searchQuery, itemType);
      console.log('API Response:', response);

      if (response && Array.isArray(response)) {
        setItems(response);
        setTotalCount(response.length);
      } else if (response && response.data) {
        setItems(response.data);
        setTotalCount(response.total || response.data.length);
      } else {
        setItems([]);
        setTotalCount(0);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Failed to fetch products');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...value,
      },
    }));
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (itemToDelete) {
        await productService.deleteProduct(itemToDelete._id);
        fetchProducts();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handleEditClick = (item) => {
    navigate(`/items/edit/${item._id}`);
  };

  const handleImportSample = async () => {
    try {
      setLoading(true);
      const response = await productService.downloadBulkSample();

      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'items_sample.xlsx');
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setError(null);
    } catch (err) {
      console.error('Error downloading sample:', err);
      setError(err.message || 'Failed to download sample file');
    } finally {
      setLoading(false);
    }
  };


  // Auto-build field mapping using Excel headers as defaults
  const buildDefaultMapping = (apiFields, excelHeaders) => {
    const mapping = {};
    apiFields.forEach((field) => {
      const fieldName = typeof field === 'object' ? (field.key || field.name || field.field || '') : field;
      // Try to find an Excel header that closely matches the API field name (case-insensitive)
      const match = excelHeaders.find(
        (h) => h.toLowerCase().replace(/[\s_-]/g, '') === fieldName.toLowerCase().replace(/[\s_-]/g, '')
      );
      mapping[fieldName] = match || '';
    });
    return mapping;
  };

  // Fetch API bulk fields and open the mapping modal
  const openMappingModal = async (file, headers, rows) => {
    setMappingModalOpen(true);
    setBulkFieldsLoading(true);
    setBulkFieldsError(null);
    try {
      const data = await productService.getBulkFields();
      // Normalise response: could be array of strings, array of objects, or { fields: [...] }
      let fields = [];
      if (Array.isArray(data)) {
        fields = data;
      } else if (data && Array.isArray(data.fields)) {
        fields = data.fields;
      } else if (data && typeof data === 'object') {
        fields = Object.keys(data);
      }
      setBulkFields(fields);
      setFieldMapping(buildDefaultMapping(fields, headers));
    } catch (err) {
      console.error('Error fetching bulk fields:', err);
      setBulkFieldsError('Could not load API fields. You can still proceed with the raw data.');
      // Fall back: use excel headers as both keys and values
      setBulkFields(headers);
      const fallback = {};
      headers.forEach((h) => { fallback[h] = h; });
      setFieldMapping(fallback);
    } finally {
      setBulkFieldsLoading(false);
    }
  };

  const handleImportExcel = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      // Parse immediately, then open mapping modal once headers are ready
      setImportError(null);
      setImportSuccess(null);
      setParsedRows([]);
      setParsedHeaders([]);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          if (rawRows.length === 0) {
            setImportError('The file is empty or has no data rows.');
            return;
          }
          const headers = Object.keys(rawRows[0]);
          setParsedHeaders(headers);
          setParsedRows(rawRows);
          // Open the field-mapping popup automatically
          openMappingModal(file, headers, rawRows);
        } catch (err) {
          console.error('SheetJS parse error:', err);
          setImportError('Could not parse the file. Make sure it is a valid Excel or CSV file.');
        }
      };
      reader.onerror = () => setImportError('Failed to read the file.');
      reader.readAsArrayBuffer(file);
    }
    event.target.value = '';
  };



  const handleCloseMappingModal = () => {
    if (!importLoading) {
      setMappingModalOpen(false);
    }
  };

  const handleMappingChange = (apiField, excelHeader) => {
    setFieldMapping((prev) => ({ ...prev, [apiField]: excelHeader }));
  };

  // Send the original Excel file as multipart/form-data to /products/bulk-upload
  const handleConfirmImport = async () => {
    if (!importFile || !parsedRows.length) return;
    setImportLoading(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      console.log('--- BULK UPLOAD INVESTIGATION ---');
      console.log('1. Parsed Excel Headers:', parsedHeaders);
      console.log('2. First Parsed Row (Before Mapping):', parsedRows[0]);
      console.log('3. Current Field Mapping:', fieldMapping);

      // Create transformed mapping and rows
      const cleanedMapping = {};
      const validationErrors = [];
      const transformedRows = [];

      for (let i = 0; i < parsedRows.length; i++) {
        const row = parsedRows[i];
        const newRow = {};

        Object.entries(fieldMapping).forEach(([apiField, excelHeader]) => {
          if (excelHeader && excelHeader.trim() !== '') {
            cleanedMapping[apiField] = apiField;

            let val = row[excelHeader];
            // Handle and clean values
            if (val !== undefined && val !== null) {
              if (typeof val === 'string') {
                val = val.trim();
              }
              // Only assign if it's not empty after trimming
              if (val !== '') {
                newRow[apiField] = val;
              }
            }
          }
        });

        // Validation for mandatory fields
        const rowNum = i + 2; // Excel row number (assuming header is row 1)
        if (!newRow.productName) {
          validationErrors.push(`Row ${rowNum}: "productName" is required but is missing or empty.`);
        }

        transformedRows.push(newRow);
      }

      if (Object.keys(cleanedMapping).length === 0) {
        throw new Error('Please map at least one field before importing.');
      }

      if (validationErrors.length > 0) {
        // Show the first few errors to the user clearly
        const errorMsg = `Validation failed for ${validationErrors.length} row(s):\n` +
          validationErrors.slice(0, 3).join('\n') +
          (validationErrors.length > 3 ? `\n...and ${validationErrors.length - 3} more errors.` : '');
        throw new Error(errorMsg);
      }

      // Pass cleanedMapping directly since productService.bulkImport wraps it in fieldMap
      const exactHeaders = Object.keys(cleanedMapping);
      const worksheet = XLSX.utils.json_to_sheet(transformedRows, { header: exactHeaders });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Items');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      const newFile = new File([excelBuffer], importFile.name, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const result = await productService.bulkImport(newFile, cleanedMapping);

      // Parse response — API may return { imported, failed, errors } or similar
      const importedCount =
        result?.imported ??
        result?.insertedCount ??
        result?.count ??
        parsedRows.length;
      const failedCount = result?.failed ?? result?.failedCount ?? 0;

      const successMsg =
        failedCount > 0
          ? `Imported ${importedCount} item(s) — ${failedCount} skipped/failed. Check your data.`
          : `Successfully Created.`;

      // Close the mapping modal, reset all import state, then show toast
      setMappingModalOpen(false);
      setParsedRows([]);
      setParsedHeaders([]);
      setFieldMapping({});
      setImportFile(null);
      setImportError(null);
      setImportSuccess(null);

      showSnackbar(successMsg, failedCount > 0 ? 'warning' : 'success');
      fetchProducts();
    } catch (err) {
      console.error('Error importing excel:', err);
      // Keep modal open so user can fix & retry without losing their mapping
      setImportError(
        err?.message || 'Import failed. Please check the file and field mapping, then try again.'
      );
    } finally {
      setImportLoading(false);
    }
  };



  // Simplified filtering logic with tab functionality
  const filteredItems = items.filter(item => {
    if (!item) return false;

    // Status filter
    const statusMatch =
      (filters.status.active && item.isActive === true) ||
      (filters.status.inactive && item.isActive === false);

    // Item type filter based on active tab
    const itemTypeMatch =
      activeTab === 0 || // All Items tab
      (activeTab === 1 && item.itemType === 'goods') || // Goods tab
      (activeTab === 2 && item.itemType === 'service'); // Services tab

    return statusMatch && itemTypeMatch;
  });

  console.log('Filtered items:', filteredItems); // Debug log

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const exportColumns = [
    { field: 'productCode', headerName: 'Code' },
    { field: 'productName', headerName: 'Name' },
    { field: 'basicUnit', headerName: 'Base Unit' },
    { field: 'group', headerName: 'Group' },
    { field: 'salesRate', headerName: 'Sales Price' },
    { field: 'barcode', headerName: 'Barcode' },
    { field: 'taxRate', headerName: 'VAT' },
    { field: 'hsnCode', headerName: 'HSN Code' },
    { field: 'isActive', headerName: 'Status' }
  ];

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Items
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            View all your items and services
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant="outlined"

            size="small"
            onClick={handleImportSample}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            <DownloadIcon />
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            size="small"
            component="label"
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            Import
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleImportExcel}
            />
          </Button>

          <ExportButton
            data={items}
            columns={exportColumns}
            filename="items-all"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => navigate('/items/new')}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
              background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)',
              },
            }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label="All Items" />
        <Tab label="Goods" />
        <Tab label="Services" />
      </Tabs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search for items..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            maxWidth: 500,
            height: 28,
            '& .MuiOutlinedInput-root': {
              borderRadius: '2px',
              height: 28,
            },
            '& .MuiInputBase-input': {
              py: 0.5,
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          size="small"
          onClick={handleFilterClick}
          sx={{
            borderRadius: '2px',
            textTransform: 'none',
            py: 0.25,
            px: 1,
            height: 28,
            ml: 1,
          }}
        >
          Filters
        </Button>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              width: 200,
              p: 1,
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>Status</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.active}
                  onChange={(e) => handleFilterChange('status', { active: e.target.checked })}
                  size="small"
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.inactive}
                  onChange={(e) => handleFilterChange('status', { inactive: e.target.checked })}
                  size="small"
                />
              }
              label="Inactive"
            />
          </FormGroup>
          {/* <Divider sx={{ my: 1 }} /> */}
          {/* <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>Group</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.group.electronics}
                  onChange={(e) => handleFilterChange('group', { electronics: e.target.checked })}
                  size="small"
                />
              }
              label="Electronics"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.group.services}
                  onChange={(e) => handleFilterChange('group', { services: e.target.checked })}
                  size="small"
                />
              }
              label="Services"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.group.furniture}
                  onChange={(e) => handleFilterChange('group', { furniture: e.target.checked })}
                  size="small"
                />
              }
              label="Furniture"
            />
          </FormGroup> */}
        </Menu>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '200px' }}>Code</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Name</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Base Unit</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Group</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Unit Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Rate + Tax</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Barcode</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>VAT</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>HSN Code</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '100px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => (
                    <TableRow
                      key={item._id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.productCode || '-'}</TableCell>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', py: 0.5 }}>
                        {item.productName || '-'}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.basicUnit || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.group || '-'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>{item.salesRate?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>{item.priceWithTax?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.barcode || '-'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>{item.taxRate || '0'}%</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.hsnCode || '-'}</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Chip
                          label={item.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={item.isActive ? 'success' : 'error'}
                          variant="outlined"
                          sx={{
                            fontWeight: 'medium',
                            backgroundColor: !item.isActive ? 'rgba(211, 47, 47, 0.1)' : 'transparent'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleEditClick(item)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleDeleteClick(item)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              '& .MuiTablePagination-select': {
                py: 0.5,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Paper>

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Item"
        message={`Are you sure you want to delete "${itemToDelete?.productName}"?`}
        confirmText="Delete"
        cancelText="Cancel"
      />



      {/* ── Field Mapping Modal ─────────────────────────────────────────── */}
      <Dialog
        open={mappingModalOpen}
        onClose={handleCloseMappingModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 700,
            fontSize: '1rem',
            background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Map Excel Columns to Product Fields
          {importFile && (
            <Typography
              variant="caption"
              sx={{ ml: 'auto', opacity: 0.85, fontWeight: 400 }}
            >
              {importFile.name} &nbsp;·&nbsp; {parsedRows.length} rows
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, mt: '20px' }}>
          {importLoading && <LinearProgress sx={{ mb: 2 }} />}

          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importError}
            </Alert>
          )}
          {bulkFieldsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {bulkFieldsError}
            </Alert>
          )}

          {/* Instruction */}
          {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Match each <strong>API field</strong> (left) to the corresponding{' '}
            <strong>Excel column</strong> (right). Headers from your file are
            pre-filled automatically — adjust if needed.
          </Typography> */}

          {bulkFieldsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading API fields…
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                maxHeight: 380,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {bulkFields.map((field) => {
                const apiField =
                  typeof field === 'object'
                    ? field.key || field.name || field.field || JSON.stringify(field)
                    : field;
                const label =
                  typeof field === 'object'
                    ? field.label || field.displayName || apiField
                    : field;
                const required =
                  typeof field === 'object' ? !!field.required : false;

                return (
                  <Box
                    key={apiField}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: '6px',
                      border: '1px solid rgba(0,0,0,0.08)',
                      bgcolor: 'rgba(67,97,238,0.02)',
                    }}
                  >
                    {/* API field name */}
                    <Box sx={{ flex: '0 0 auto', minWidth: 140 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: required ? 'error.main' : 'text.primary',
                          fontSize: '0.78rem',
                        }}
                      >
                        {label}
                        {required && (
                          <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                            *
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    {/* Arrow */}
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.disabled', flex: '0 0 auto' }}
                    >
                      ←
                    </Typography>

                    {/* Excel header selector */}
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={fieldMapping[apiField] || ''}
                        onChange={(e) => handleMappingChange(apiField, e.target.value)}
                        displayEmpty
                        sx={{
                          fontSize: '0.8rem',
                          '& .MuiOutlinedInput-notchedOutline': { borderRadius: '4px' },
                        }}
                      >
                        <MenuItem value="">
                          <em style={{ color: '#999', fontSize: '0.8rem' }}>— not mapped —</em>
                        </MenuItem>
                        {parsedHeaders.map((h) => (
                          <MenuItem key={h} value={h} sx={{ fontSize: '0.82rem' }}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Mini preview */}
          {/* {parsedRows.length > 0 && parsedHeaders.length > 0 && !bulkFieldsLoading && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75 }}
              >
                Preview — first 3 rows of your Excel file
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid rgba(0,0,0,0.10)', borderRadius: '4px', maxHeight: 160 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {parsedHeaders.map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontWeight: 700,
                            bgcolor: 'rgba(67,97,238,0.07)',
                            py: 0.5,
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.slice(0, 3).map((row, idx) => (
                      <TableRow key={idx}>
                        {parsedHeaders.map((h) => (
                          <TableCell
                            key={h}
                            sx={{
                              py: 0.4,
                              fontSize: '0.75rem',
                              maxWidth: 140,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {String(row[h] ?? '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )} */}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1,
            borderTop: '1px solid rgba(0,0,0,0.08)',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseMappingModal}
            disabled={importLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={parsedRows.length === 0 || importLoading || bulkFieldsLoading}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)',
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Success / Warning Toast ─────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '8px', boxShadow: 4 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Items; 